import React from "react";
import PropTypes from "prop-types";

import Spinner from "../Spinner";

import { darkenColor, hexToRgb } from "../../../Utils/CommonFunctions";

export default function Button({
  children, variant = "primary", size = "md", loading = false, icon, iconPosition = "left", className = "",
  theme = {}, isActive, disabled, isMobileTab = false, type, onClick, ...props }) {
  // Default to fallback values if theme or theme.bank or theme.brand is null/undefined
  const getColor = (primary, secondary, defaultText) => {
    return primary ? `#${primary}` : secondary ? `#${secondary}` : `#${defaultText}`;
  };

  const primaryColor = getColor(theme?.bank?.hex_code, theme?.brand?.hex_code, "F5437E");
  const borderColor = getColor(theme?.bank?.hex_code, theme?.brand?.hex_code, "ED1C24");
  const textColor = getColor(theme?.bank?.text_hex_code, theme?.brand?.text_hex_code, "ffffff");

  const sizeClasses = {
    sm: "h-10 p-0",
    md: "h-12 px-6 text-base",
    lg: "h-14 px-8 text-lg",
    xl: "h-16 px-10 text-xl",
    full: "w-full h-16 py-4 text-xl",
    tab: isMobileTab
      ? "w-full pt-0"
      : "w-full md:w-auto lg:text-sm xl:text-lg pt-0 md:p-3",
    moduleTab: isMobileTab
      ? "w-full px-2.5"
      : "w-full md:w-auto px-5 lg:text-sm xl:text-lg md:p-3",
  }[variant === "tab" ? "tab" : variant === "moduleTab" ? "moduleTab" : size] || "h-12 px-6 text-base";

  const getStyles = () => {
    const baseStyles = {
      backgroundColor: primaryColor,
      borderColor: darkenColor(borderColor, 40),
      color: textColor,
    };

    if (variant === "primary") {
      if (disabled) {
        baseStyles.backgroundColor = "#D9D9D9";
        baseStyles.borderColor = "#D9D9D9";
        baseStyles.cursor = "not-allowed";
        baseStyles.color = "#000000";
      }
      if (loading) {
        const [r, g, b] = hexToRgb(primaryColor);
        baseStyles.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.5)`;
        baseStyles.borderColor = `rgba(${r}, ${g}, ${b}, 0.5)`;
        baseStyles.cursor = "not-allowed";
      }
    } else if (variant === "secondary") {
      baseStyles.backgroundColor = "#ffffff";
      baseStyles.borderColor = "#1e1e1e";
      baseStyles.color = "#1e1e1e";
    } else if (variant === "outline") {
      baseStyles.backgroundColor = "transparent";
      baseStyles.borderColor = "#1e1e1e";
      baseStyles.color = "#1e1e1e";
    } else if (variant === "disabled") {
      baseStyles.backgroundColor = "#D9D9D9";
      baseStyles.borderColor = "#D9D9D9";
      baseStyles.color = "black";
      baseStyles.cursor = "not-allowed";
    } else if (variant === "tab") {
      baseStyles.backgroundColor = "transparent";
      if (!isMobileTab) {
        baseStyles.borderWidth = "0 0 3px 0";
      }
      baseStyles.borderColor = isActive ? primaryColor : "transparent";
      baseStyles.color = isActive ? primaryColor : "#1E1E1E";
      baseStyles.fontWeight = isActive ? "600" : "normal";
      baseStyles.borderRadius = "0";
    } else if (variant === "moduleTab") {
      // New variant for module tabs
      if (isActive) {
        baseStyles.backgroundColor = primaryColor;
        baseStyles.color = textColor;
        baseStyles.borderColor = darkenColor(borderColor, 40);
        baseStyles.cursor = "pointer";
      } else {
        baseStyles.backgroundColor = "transparent";
        baseStyles.color = "#1E1E1E";
        baseStyles.borderColor = "transparent";
        baseStyles.cursor = "pointer";
      }
    }

    return baseStyles;
  };

  const baseClasses = variant === "tab" || variant === "moduleTab"
    ? `font-Inter ${isMobileTab ? 'flex justify-center' : 'flex lg:items-center justify-center'} rounded-[10px] transition-all duration-200 ease-in-out`
    : "font-Inter rounded-[10px] border transition-all duration-200 ease-in-out";

  const iconClasses = icon && variant !== "tab" && variant !== "moduleTab" ? "flex items-center justify-center gap-2" : "";

  // Update icon styles for mobile tab
  const getIconClassName = (originalIcon) => {
    if ((variant === "tab" || variant === "moduleTab") && isMobileTab) {
      return React.cloneElement(originalIcon, {
        className: `${originalIcon.props.className || ''} block`,
        style: {
          color: isActive ? primaryColor : "#585757",
          fill: isActive ? primaryColor : "#585757",
        },
      });
    }
    return originalIcon;
  };

  return (
    <button
      className={`${baseClasses} ${sizeClasses} ${iconClasses} ${className}`}
      style={getStyles()}
      disabled={disabled || loading}
      type={type}
      onClick={onClick}
      {...props}
    >
      {loading ? (
        <Spinner className="animate-spin" color={textColor} />
      ) : (
        <>
          {icon && iconPosition === "left" && getIconClassName(icon)}
          {(!isMobileTab || !(variant === "tab" || variant === "moduleTab")) && children}
          {icon && iconPosition === "right" && getIconClassName(icon)}
        </>
      )}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "outline",
    "disabled",
    "tab",
    "moduleTab",
  ]),
  size: PropTypes.oneOf(["sm", "md", "lg", "xl", "full"]),
  loading: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  className: PropTypes.string,
  theme: PropTypes.shape({
    bank: PropTypes.shape({
      hex_code: PropTypes.string,
      text_hex_code: PropTypes.string,
    }),
  }),
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
};